import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';

function PandaMark() {
  return (
    <Box sx={{ position: 'relative', width: { xs: 62, md: 88 }, height: { xs: 58, md: 82 }, flex: '0 0 auto' }}>
      <Box sx={{ position: 'absolute', left: 6, top: 2, width: 20, height: 20, borderRadius: '50%', bgcolor: '#111827' }} />
      <Box sx={{ position: 'absolute', right: 8, top: 2, width: 20, height: 20, borderRadius: '50%', bgcolor: '#111827' }} />
      <Box
        sx={{
          position: 'absolute',
          left: 6,
          top: 8,
          width: { xs: 48, md: 68 },
          height: { xs: 48, md: 68 },
          borderRadius: '50%',
          bgcolor: '#fff',
          boxShadow: '0 4px 0 rgba(15,23,42,0.18)',
        }}
      >
        <Box sx={{ position: 'absolute', left: '17%', top: '27%', width: '26%', height: '30%', borderRadius: '50%', bgcolor: '#111827', transform: 'rotate(-18deg)' }} />
        <Box sx={{ position: 'absolute', right: '17%', top: '27%', width: '26%', height: '30%', borderRadius: '50%', bgcolor: '#111827', transform: 'rotate(18deg)' }} />
        <Box sx={{ position: 'absolute', left: '28%', top: '36%', width: 6, height: 6, borderRadius: '50%', bgcolor: '#fff' }} />
        <Box sx={{ position: 'absolute', right: '28%', top: '36%', width: 6, height: 6, borderRadius: '50%', bgcolor: '#fff' }} />
        <Box sx={{ position: 'absolute', left: '44%', top: '56%', width: '12%', height: '9%', borderRadius: '50%', bgcolor: '#111827' }} />
        <Box sx={{ position: 'absolute', left: '39%', top: '66%', width: '22%', height: 8, borderBottom: '2px solid #111827', borderRadius: '0 0 99px 99px' }} />
      </Box>
      <Box sx={{ position: 'absolute', right: { xs: -5, md: -7 }, top: { xs: 18, md: 25 }, width: 18, height: 9, borderRadius: 99, bgcolor: '#fff', transform: 'rotate(38deg)', boxShadow: '0 0 0 3px #111827' }} />
    </Box>
  );
}

function BrandWord() {
  const letters = [
    ['D', '#ef5b8a'],
    ['A', '#f59e0b'],
    ['N', '#84cc16'],
    ['D', '#22c55e'],
    ['U', '#38bdf8'],
  ];
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: { xs: 0.05, md: 0.2 } }}>
      {letters.map(([letter, color]) => (
        <Box
          key={letter + color}
          component="span"
          sx={{
            color,
            WebkitTextStroke: { xs: '1px #fff', md: '2px #fff' },
            textShadow: '0 3px 0 rgba(15,23,42,0.16)',
            fontWeight: 1000,
          }}
        >
          {letter}
        </Box>
      ))}
    </Box>
  );
}

function HeaderCloud() {
  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'block' },
        position: 'absolute',
        left: '69%',
        top: 30,
        width: 64,
        height: 24,
        borderRadius: 99,
        bgcolor: '#fff',
        filter: 'drop-shadow(0 4px 0 rgba(38,20,100,0.18))',
        '&::before, &::after': {
          content: '""',
          position: 'absolute',
          bgcolor: '#fff',
          borderRadius: '50%',
        },
        '&::before': { width: 28, height: 28, left: 10, top: -13 },
        '&::after': { width: 34, height: 34, right: 8, top: -18 },
      }}
    />
  );
}

export default function BoardHeader({ classTitle, mode = 'board', showTeacherButton = true, onTeacherClick, onRefresh, soundEnabled, onToggleSound, onLogout, logoutDisabled = false }) {
  return (
    <Box
      sx={{
        position: 'relative',
        mx: { xs: -1.2, sm: -2, md: -3.2 },
        mb: { xs: 1.2, md: 1.5 },
        minHeight: { xs: 76, md: 108 },
        borderRadius: '0 0 28px 28px',
        background: 'linear-gradient(180deg, #6d45df 0%, #5128bc 100%)',
        color: '#fff',
        px: { xs: 1.4, sm: 2.4, md: 4 },
        py: { xs: mode === 'teacher' ? 1.2 : 0, md: 0 },
        display: 'flex',
        flexDirection: { xs: mode === 'teacher' ? 'column' : 'row', md: 'row' },
        alignItems: { xs: mode === 'teacher' ? 'stretch' : 'center', md: 'center' },
        justifyContent: 'space-between',
        gap: { xs: 1, md: 2 },
        boxShadow: '0 6px 0 rgba(39,22,108,0.24), 0 14px 28px rgba(88,28,135,0.18)',
        overflow: 'hidden',
      }}
    >
      <HeaderCloud />
      <StarRoundedIcon
        sx={{
          display: { xs: 'none', md: 'block' },
          position: 'absolute',
          left: '59%',
          top: 29,
          fontSize: 44,
          color: '#facc15',
          filter: 'drop-shadow(0 4px 0 rgba(76,29,149,0.34))',
          transform: 'rotate(-12deg)',
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 2 }, minWidth: 0, zIndex: 1 }}>
        <PandaMark />
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            gap: { xs: 1.2, md: 2.4 },
            fontWeight: 1000,
            lineHeight: 1,
            whiteSpace: { xs: 'normal', sm: 'nowrap' },
            flexWrap: { xs: 'wrap', md: 'nowrap' },
            fontSize: { xs: 31, sm: 42, md: 58 },
            letterSpacing: 0,
            textShadow: '0 3px 0 rgba(15,23,42,0.16)',
          }}
        >
          <BrandWord />
          <Box component="span" sx={{ color: '#fff', fontSize: { xs: 28, sm: 38, md: 52 } }}>
            Mini Görevler
          </Box>
        </Typography>
      </Box>
    
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: mode === 'teacher' ? 'wrap' : 'nowrap', justifyContent: mode === 'teacher' ? 'space-between' : undefined, gap: { xs: 0.8, md: 1.2 }, zIndex: 1 }}>
        {mode === 'teacher' ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: .5, md: .8 }, bgcolor: '#85e6af', color: '#174736', borderRadius: 99, px: { xs: 1.5, md: 2.5 }, py: 1.2, fontWeight: 900, fontSize: { xs: 14, md: 20 }, whiteSpace: 'nowrap' }}>
            <LockRoundedIcon sx={{ fontSize: { xs: 18, md: 23 } }} />Öğretmen Modu
          </Box>
        ) : <>
        {showTeacherButton && <Button onClick={onTeacherClick} startIcon={<LockRoundedIcon />} sx={{ color: '#fff', bgcolor: 'rgba(39,22,108,0.48)', borderRadius: 99, px: { xs: 1.5, md: 2.5 }, py: 1.2, fontWeight: 900, fontSize: { xs: 14, md: 20 }, '&:hover': { bgcolor: 'rgba(39,22,108,0.7)' } }}>Öğretmen</Button>}
        <IconButton
          aria-label="Yenile"
          onClick={onRefresh}
          sx={{
            width: { xs: 46, md: 58 },
            height: { xs: 46, md: 58 },
            bgcolor: 'rgba(255,255,255,0.15)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.18)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
          }}
        >
          <RefreshRoundedIcon />
        </IconButton>
        <IconButton
          aria-label={soundEnabled ? 'Sesi kapat' : 'Sesi aç'}
          onClick={onToggleSound}
          sx={{
            width: { xs: 46, md: 58 },
            height: { xs: 46, md: 58 },
            bgcolor: 'rgba(255,255,255,0.15)',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.18)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
          }}
        >
          {soundEnabled ? <VolumeUpRoundedIcon /> : <VolumeOffRoundedIcon />}
        </IconButton>
        </>}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.7, md: 1 },
            bgcolor: 'rgba(39,22,108,0.48)',
            borderRadius: 99,
            px: { xs: 1.1, md: 2.2 },
            py: { xs: 0.9, md: 1.15 },
            minWidth: { xs: 0, md: 210 },
            justifyContent: 'center',
            boxShadow: 'inset 0 -3px 0 rgba(15,23,42,0.16)',
          }}
        >
          <GroupsRoundedIcon sx={{ fontSize: { xs: 22, md: 30 } }} />
          <Typography sx={{ fontWeight: 1000, fontSize: { xs: 18, md: 28 }, lineHeight: 1, whiteSpace: 'nowrap' }}>{classTitle}</Typography>
        </Box>
        {mode === 'teacher' && <Button onClick={onLogout} disabled={logoutDisabled} startIcon={<LogoutRoundedIcon />} variant="outlined" color="inherit" sx={{ color: '#fff', backgroundColor: 'rgba(39,22,108,0.35)', borderColor: 'rgba(255,255,255,.65)', borderRadius: 99, px: { xs: 1.5, md: 2.5 }, py: 1.1, fontWeight: 900, fontSize: { xs: 14, md: 20 }, textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { borderColor: '#fff', backgroundColor: 'rgba(39,22,108,0.55)' }, '&.Mui-disabled': { color: 'rgba(255,255,255,.55)', borderColor: 'rgba(255,255,255,.3)' } }}>Çıkış</Button>}
      </Box>
    </Box>
  );
}
